import { LowerCasePipe } from '@angular/common';
import { Component, computed, ElementRef, Injector, input, OnInit, output, signal, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { injectVirtualizer } from '@tanstack/angular-virtual';
import { combineLatest, map, takeWhile } from 'rxjs';
import { RadioButtonComponent } from '../../../../../../../shared/components/forms/radio-button/radio-button.component';
import { TextSearchComponent } from '../../../../../../../shared/components/forms/text-search/text-search.component';
import { templateDrivenFormsViewProviders } from '../../../../../../../shared/directives/form.directive';
import { stripProductTier } from '../../../../../../../shared/utils/formatting';
import { GAService } from '../../../../services/ga-tags.service';
import { OrbitFormClaimService } from '../../../../services/orbit-form-claim.service';
import { FaultData, FaultDataPossibleAnswer } from '../../../../types/inspect-bundle';
import { getErrorsFromSubmission } from '../../pipes/error-subscription';
import { formValidationGenerator, FormValidators } from '../../pipes/form-validation';
import { SmartFixEmbeddedFix } from '../smart-fix-embedded-fix/smart-fix-embedded-fix.component';

@Component({
  selector: 'app-fault-details',
  imports: [FormsModule, RadioButtonComponent, TextSearchComponent, LowerCasePipe, SmartFixEmbeddedFix],
  templateUrl: './fault-details.component.html',
  styleUrl: './fault-details.component.scss',
  viewProviders: [templateDrivenFormsViewProviders],
})
export class FaultDetailsComponent implements OnInit {
  scrollElement = viewChild<ElementRef<HTMLDivElement>>('categoryScrollElement');
  faultScrollElement = viewChild<ElementRef<HTMLDivElement>>('faultScrollElement');
  smartFixEmbeddedFix = viewChild<SmartFixEmbeddedFix>('smartFixEmbeddedContent');

  faultCategory: FaultData | undefined = undefined;
  faultDetails: FaultDataPossibleAnswer | undefined = undefined;
  formSubmitted = input(false);
  formValue = input<Record<string, any>>({});
  formValid = output<boolean>();

  errors = signal<any>({});
  disableFreeTextSearch = signal(false);

  private _destroyed = false;

  private viewModel = computed(() => {
    const hasUsableQuestion = this.orbitFormClaimService.state.claimData?.mandatoryData?.displayApplianceUseable;
    const isFreeTextSearch =
      this.orbitFormClaimService.state.claimData?.isFreeTextSearch && !this.disableFreeTextSearch();
    let faultOptions: FaultData[] | FaultData = this.orbitFormClaimService.state.claimData?.mandatoryData?.faultData;
    faultOptions = Array.isArray(faultOptions) ? faultOptions : [faultOptions];
    const productGroupCode = this.orbitFormClaimService.state.claimData?.mandatoryData?.oEMProductGroupCode?.substring(
      2,
      6
    );
    const productDescription = stripProductTier(this.orbitFormClaimService.state.claimData?.mandatoryData?.productType);
    const goodsColour = this.orbitFormClaimService.state?.claimData?.bundle?.goodsColour;
    const showFaultCategorySelection = faultOptions.length > 1;
    const isHeatingPlan = goodsColour === 'H';

    if (faultOptions.length === 1 && !this.faultCategory) {
      this.faultCategory = faultOptions[0];
    }

    return {
      isFreeTextSearch,
      productGroupCode,
      faultOptions,
      showFaultCategorySelection: showFaultCategorySelection,
      faultStep: showFaultCategorySelection ? '2' : '1',
      formValue: this.formValue(),
      faultCategory: faultOptions.map((i) => {
        return {
          title: i.faultCategoryLabel,
          value: i,
        };
      }),
      faultDetailsActive: !!this.formValue()?.['faultCategory'],
      faultDetails: (this.formValue()?.['faultCategory']?.possibleAnswers ?? [])
        .map((i: FaultDataPossibleAnswer) => {
          return {
            title: i.faultLabel,
            value: i,
          };
        })
        .filter((i: { title: string; value: FaultDataPossibleAnswer }) => {
          const faultsToRemove = ['gas leak', 'annual service', 'smell of gas', 'smell of burning', 'smell of fumes'];
          return !faultsToRemove.includes(i.title.toLowerCase());
        }),
      hasUsableQuestion: hasUsableQuestion,
      showUsabilityQuestion:
        ((isFreeTextSearch && this.formValue()?.['freeTextSearch']) || this.formValue()?.['faultDetails']) &&
        hasUsableQuestion,
      showWarningBox: hasUsableQuestion
        ? this.formValue()?.['applianceUsable'] !== undefined
        : !!this.faultDetails || (isFreeTextSearch && !!this.formValue()?.['freeTextSearch']),
      submittingResource: this.orbitFormClaimService.formSubmission,
      productDescription,
      errors: this.errors(),
      formSubmitted: this.formSubmitted(),
      isHeatingPlan: isHeatingPlan,
    };
  });

  get vm() {
    return this.viewModel();
  }

  categoryVirtualiser = injectVirtualizer(() => ({
    scrollElement: this.scrollElement(),
    count: this.vm.faultCategory.length,
    estimateSize: () => 71,
    overscan: 5,
  }));

  faultVirtualiser = injectVirtualizer(() => ({
    scrollElement: this.faultScrollElement(),
    count: this.vm.faultDetails.length,
    estimateSize: () => 71,
    overscan: 5,
  }));

  constructor(
    private readonly injector: Injector,
    private readonly orbitFormClaimService: OrbitFormClaimService,
    private readonly gaService: GAService
  ) {}

  ngOnInit() {
    this.handleFormValidation();
    this.handleAPIErrors();
    this.trackPageLoad();
  }

  handleFormValidation(): void {
    const fieldValidation: Record<string, ((value: any) => Record<string, any> | null)[]> = {
      freeTextSearch: [(value: any) => (this.vm.isFreeTextSearch ? FormValidators.required(value) : null)],
      faultCategory: [(value: any) => (!this.vm.isFreeTextSearch ? FormValidators.required(value) : null)],
      faultDetails: [(value: any) => (!this.vm.isFreeTextSearch ? FormValidators.required(value) : null)],
      applianceUsable: [
        (value: any) =>
          this.vm.showUsabilityQuestion && !this.vm.isHeatingPlan ? FormValidators.required(value) : null,
      ],
    };

    const formValue$ = toObservable(this.formValue, { injector: this.injector });
    const freeTextDisabled$ = toObservable(this.disableFreeTextSearch, { injector: this.injector });

    combineLatest([formValue$, freeTextDisabled$])
      .pipe(
        map(([fv]) => fv),
        formValidationGenerator(fieldValidation, []),
        takeWhile(() => !this._destroyed)
      )
      .subscribe({
        next: ({ isFormValid, errors }) => {
          this.formValid.emit(isFormValid);
          this.errors.set(errors);
        },
      });
  }

  handleAPIErrors(): void {
    getErrorsFromSubmission(this.vm.submittingResource, this.injector, (attempts, error) => {
      console.log({ error });
    });
  }

  onSearchError() {
    this.disableFreeTextSearch.set(true);
  }

  trackPageLoad() {
    this.gaService.sendGATag(this.gaService.GA_FAULT_CAPTURE.PAGE_LOAD());
  }

  get eligibleForSmartFixEmbeddedContentPanel(): boolean {
    if (!this.vm.productDescription) return false;
    const eligibleProductTypes = ['tumble dryer', 'washing machine', 'washer dryer'];
    return eligibleProductTypes.includes(this.vm?.productDescription?.toLowerCase());
  }
}
